/**
 * ================================================================================================
 *  THE LANGUAGE, AND THE DIRECTION THAT FOLLOWS FROM IT. **Master §28.**
 * ================================================================================================
 *
 * §28: Hebrew default RTL, English toggle LTR, and the language is USER-LEVEL.
 *
 * ── DIRECTION IS DERIVED, NEVER SET SEPARATELY ──────────────────────────────────────────────────
 *
 * `index.html` ships `lang="he" dir="rtl"` on the document, which is right for the default and
 * wrong the moment anyone switches. Both attributes are updated here from ONE value, because a
 * language and a direction kept as two settings are two settings that can disagree -- and an RTL
 * document rendering English is not a cosmetic fault, it is unreadable.
 *
 * ── WHERE THE PREFERENCE LIVES, AND WHAT IS HONESTLY MISSING ────────────────────────────────────
 *
 * §28 says USER-level. The product has no route that stores a user preference -- there is no
 * `user.preferences.update` in the registry's 104 routes -- so this keeps the choice in
 * `localStorage`, which is PER DEVICE and not per user.
 *
 * That difference is real and is not papered over: the same person on a second machine gets the
 * default again. Closing it needs a server route and a column, which is a product change with its
 * own decision, and inventing one here would put a user preference in a place the Master does not
 * put it. Recorded rather than quietly accepted.
 *
 * Storage is wrapped because it throws in a private window and returns null with site data
 * cleared -- a language toggle must never be the reason a screen fails to render.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { DEFAULT_LOCALE, DIRECTION, LOCALES, text, type Locale, type TextKey } from './catalogue';

const STORAGE_KEY = 'glpoint.locale';

interface LocaleValue {
  readonly locale: Locale;
  readonly setLocale: (next: Locale) => void;
  /** The string for this key in the active language. */
  readonly t: (key: TextKey) => string;
}

const LocaleContext = createContext<LocaleValue | null>(null);

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/** Read the remembered choice, or the default. Never throws. */
function remembered(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : DEFAULT_LOCALE;
  } catch {
    // Private window, blocked site data, or a preview with no storage at all.
    return DEFAULT_LOCALE;
  }
}

export function LocaleProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(remembered);

  /*
   * THE DOCUMENT FOLLOWS THE LANGUAGE. Both attributes, from the one value, on every change --
   * including the first render, because the remembered choice may differ from what `index.html`
   * shipped and the gap would otherwise last until the first toggle.
   */
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = DIRECTION[locale];
  }, [locale]);

  const setLocale = useCallback((next: Locale): void => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The choice still applies to this session; it simply will not be remembered.
    }
  }, []);

  const value = useMemo<LocaleValue>(
    () => ({ locale, setLocale, t: (key: TextKey) => text(key, locale) }),
    [locale, setLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/**
 * The hook every screen uses.
 *
 * IT THROWS OUTSIDE THE PROVIDER rather than defaulting to Hebrew. A silent default would render
 * a screen that looks correct in the default language and ignores the toggle -- the failure would
 * be invisible to anyone testing in Hebrew, which is everyone by default.
 */
export function useLocale(): LocaleValue {
  const value = useContext(LocaleContext);
  if (value === null) {
    throw new Error('useLocale was called outside LocaleProvider; the shell must wrap the app');
  }
  return value;
}
